import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";
import MaterialTable, { MTableToolbar } from "material-table";
import { ToastContainer } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Helper from "../../../Helper";
import authHeader from "../../../services/auth-header";
import { API } from "../../../backend";
import { getTrans as t } from "../../../Localization/translations";
import lineOfBusinessService from "../../../services/company/lineofbusinessService";
import subcompanyService from "../../../services/company/subcompanyService";
import Select from "react-select";



const BalanceSheet = () => {
  const tableRef = useRef();
  const date = new Date();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [state1, setState] = useState({ isPaneOpen: false });
  const [subcompany, setSubcompany] = useState("");
  const [lineOfBusiness, setLineOfBusiness] = useState("");
  const [amountDate, setAmountDate] = useState(null);

  const [subcompanyOptions, setSubcompanyOptions] = useState([]);
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState([]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [period1Date, setPeriod1Date] = useState(null);
  const [period2Date, setPeriod2Date] = useState(null);




  /* ================= DATE CALCULATION (BASED ON SELECTED DATE) ================= */

  const baseDate = amountDate || new Date(); // fallback to today

  const currentDateObj = new Date(baseDate);

  const prevMonthDateObj = new Date(baseDate);
  prevMonthDateObj.setMonth(prevMonthDateObj.getMonth() - 1);

  const prevYearDateObj = new Date(baseDate);
  prevYearDateObj.setFullYear(prevYearDateObj.getFullYear() - 1);

  const currentDateText = Helper.DatePicker(currentDateObj);
  const prevMonthDateText = Helper.DatePicker(prevMonthDateObj);
  const prevYearDateText = Helper.DatePicker(prevYearDateObj);

  const [selectedContract, setSelectedContract] = useState(null);
  const [contractOptions, setContractOptions] = useState([]);
  const [contractInput, setContractInput] = useState("");
  const [contractPage, setContractPage] = useState(1);
  const [contractLoading, setContractLoading] = useState(false);

  // current | prevMonth | prevYear

  /* ================= DATE CALCULATION ================= */



  /* ================= FETCH FILTER OPTIONS ================= */

  useEffect(() => {
    subcompanyService.getSubcompany().then((res) => {
      setSubcompanyOptions(res.data || []);
    });

    lineOfBusinessService.getLineOfBusiness().then((res) => {
      setLineOfBusinessOptions(res.data || []);
    });
  }, []);



  const loadContracts = async (value, page) => {
    try {
      if (!value) return;

      setContractLoading(true);

      const res = await fetch(
        `${API}contracts/contracts/filter/?id=${value}&page=${page}`,
        { headers: authHeader() }
      );

      const data = await res.json();

      if (data?.data?.length > 0) {
        setContractOptions((prev) => [...prev, ...data.data]);
        setContractPage((prev) => prev + 1);
      }

      setContractLoading(false);
    } catch (err) {
      console.error("Contract load error:", err);
      setContractLoading(false);
    }
  };

  const handleContractInputChange = (value, action) => {
    if (action.action === "input-change") {
      setContractInput(value);
      setContractPage(1);
      setContractOptions([]);
      loadContracts(value, 1);
    }
  };

  const handleContractScroll = () => {
    loadContracts(contractInput, contractPage);
  };

  const handleContractChange = (value) => {
    setSelectedContract(value);
  };



  const buildParams = () => {
    const p = new URLSearchParams();

    if (currentDate)
      p.append("current_date", Helper.DatePicker(currentDate));

    if (period1Date)
      p.append("period1_date", Helper.DatePicker(period1Date));

    if (period2Date)
      p.append("period2_date", Helper.DatePicker(period2Date));

    if (subcompany)
      p.append("subcompany", subcompany);

    if (lineOfBusiness)
      p.append("line_of_business", lineOfBusiness);

    if (selectedContract)
      p.append("contract_number", selectedContract.id);

    return p.toString();
  };



  /* ================= FETCH BALANCE SHEET ================= */

  const fetchBalanceSheet = async () => {
    setLoading(true);

    try {
      const res = await axios.get(
        `${API}accounting/balance-sheet/?${buildParams()}`,
        { headers: authHeader() }
      );

      setData(res.data);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };





  /* Re-fetch when mode or filters change */
  useEffect(() => {
    fetchBalanceSheet();
  }, [subcompany, lineOfBusiness, currentDate, period1Date, period2Date]);





  /* ================= BUILD TABLE ROWS ================= */

  const buildRows = () => {
    if (!data) return [];

    const rows = [];

    const pushSection = (title, key) => {
      rows.push({
        account: title.toUpperCase(),
        isHeader: true,
      });

      const current = data.current?.[key] || [];
      const period1 = data.period1?.[key] || [];
      const period2 = data.period2?.[key] || [];



      current.forEach((item, i) => {
        rows.push({
          account: item.name,
          as_on_date: Helper.Amount(item.total),
          period1: period1Date
            ? Helper.Amount(period1[i]?.total || 0)
            : "",

          period2: period2Date
            ? Helper.Amount(period2[i]?.total || 0)
            : "",


        });
      });

      rows.push({
        account: `Total ${title}`,
        as_on_date: Helper.Amount(data.current.totals[key]),
        period1: period1Date
          ? Helper.Amount(data.period1?.totals?.[key] || 0)
          : "",

        period2: period2Date
          ? Helper.Amount(data.period2?.totals?.[key] || 0)
          : "",


        isTotal: true,
      });
    };

    pushSection("Assets", "assets");
    pushSection("Liabilities", "liabilities");
    pushSection("Equity", "equity");

    rows.push({
      account: "Total Liabilities and Equity",
      as_on_date: Helper.Amount(
        data.current.totals.liabilities + data.current.totals.equity
      ),
      period1: period1Date
        ? Helper.Amount(
          (data.period1?.totals.liabilities || 0) +
          (data.period1?.totals.equity || 0)
        )
        : "",

      period2: period2Date
        ? Helper.Amount(
          (data.period2?.totals.liabilities || 0) +
          (data.period2?.totals.equity || 0)
        )
        : "",

      isTotal: true,
    });

    return rows;
  };


  /* ================= RENDER ================= */

  return (
    <div className="">
      <div className="container-fluid py-4 pt-2">
        <div className="row py-2 mt-2">
          <ToastContainer />
          <div className="col-md-12">
            <div className=" mb-0">
              <div className="body">
                <div className="p-0">
                  <MaterialTable
                    tableRef={tableRef}
                    style={{
                      padding: "0.5rem 0.5rem",
                      backgroundColor: "var(--bs-table-bg)",
                      borderBottomWidth: "1px",
                      boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                    }}
                    title={
                      <h5 className="mb-0">
                        {t("Balance Sheet")} <br />
                        {/* <h6 className="mb-0">
                          {t("Date : ")}
                          {date ? Helper.DateNormal(date) : ""}
                        </h6> */}
                      </h5>
                    }
                    columns={[
                      {
                        title: (
                          <div>
                            {t("Account Class")}
                          </div>
                        ),
                        field: "account",
                        headerStyle: { fontWeight: "bold", textAlign: "left" },
                        cellStyle: { fontSize: 12, textAlign: "left" },
                      },
                      {
                        title: (
                          <div className="text-end">
                            {t("As on Date")}
                            <div style={{ fontSize: 11, fontWeight: "normal" }}>
                              {currentDate
                                ? Helper.Dateview(Helper.DatePicker(currentDate))
                                : ""}

                            </div>
                          </div>
                        ),
                        field: "as_on_date",
                        headerStyle: { fontWeight: "bold", textAlign: "right" },
                        cellStyle: { fontSize: 12, textAlign: "right" },
                      },
                      {
                        title: (
                          <div className="text-end">
                            {t("Period 1")}
                            <div style={{ fontSize: 11, fontWeight: "normal" }}>
                              {period1Date
                                ? Helper.Dateview(Helper.DatePicker(period1Date))
                                : ""}

                            </div>
                          </div>
                        ),
                        field: "period1",
                        headerStyle: { fontWeight: "bold", textAlign: "right" },
                        cellStyle: { fontSize: 12, textAlign: "right" },
                      },
                      {
                        title: (
                          <div className="text-end">
                            {t("Period 2")}
                            <div style={{ fontSize: 11, fontWeight: "normal" }}>
                              {period2Date
                                ? Helper.Dateview(Helper.DatePicker(period2Date))
                                : ""}

                            </div>
                          </div>
                        ),
                        field: "period2",
                        headerStyle: { fontWeight: "bold", textAlign: "right" },
                        cellStyle: { fontSize: 12, textAlign: "right" },
                      },
                    ]}

                    data={buildRows()}
                    components={{
                      Toolbar: (props) => (
                        <div className="MuiToolbar-root MTableToolbar-spacer-21 my-temp-class">
                          <MTableToolbar {...props} />
                          <span
                            onClick={() => setState({ isPaneOpen: true })}
                            style={{ cursor: "pointer", paddingRight: "10px" }}
                          >
                            <img
                              src={process.env.PUBLIC_URL + "/static/images/ic-filter.svg"}
                              alt=""
                            />
                          </span>
                        </div>
                      ),
                    }}
                    options={{
                      headerStyle: {
                        backgroundColor: "white",
                        color: "#245C7F",
                        fontSize: 12,
                        fontWeight: "bold",
                      },

                      // IMPORTANT: allow row colors to show properly
                      bodyStyle: {
                        backgroundColor: "transparent",
                      },

                      // ✅ ROW COLORS (zebra + headers + totals)
                      rowStyle: (rowData, index) => {
                        const zebraColor = index % 2 === 1 ? "#ffffff" : "#f5f6f8";

                        // Section headers: ASSETS / LIABILITIES / EQUITY
                        if (rowData.isHeader) {
                          return {
                            backgroundColor: "#eef1f4",
                            fontWeight: "bold",
                          };
                        }

                        // Total rows
                        if (rowData.isTotal) {
                          return {
                            backgroundColor: zebraColor,
                            fontWeight: "bold",
                          };
                        }

                        // Normal rows
                        return {
                          backgroundColor: zebraColor,
                        };
                      },

                      sorting: true,
                      search: true,
                      toolbar: true,

                      // ❌ NO pagination
                      paging: false,

                      emptyRowsWhenPaging: false,

                      exportAllData: true,
                      exportButton: true,
                      exportFileName: "BalanceSheet",

                      // ✅ CSV EXPORT
                      exportCsv: (columns, data) => {
                        try {
                          const visible = Helper.visibleColumn(columns);

                          // Header mapping (safe text only)
                          const headerMap = {
                            account: "Account Class",
                            as_on_date: "As on Date",
                            prev_month: "Previous Month",
                            prev_year: "Previous Year",
                          };

                          // CSV escape helper
                          const escapeCSV = (value) => {
                            if (value === null || value === undefined) return '""';

                            const str = String(value).replace(/"/g, '""');
                            return `"${str}"`;
                          };

                          // headers (NO JSX)
                          const headers = visible.map(col =>
                            escapeCSV(headerMap[col.field] || col.field)
                          );

                          // rows
                          const rows = data.map(row =>
                            visible.map(col =>
                              escapeCSV(row[col.field])
                            )
                          );

                          const csv =
                            headers.join(",") +
                            "\n" +
                            rows.map(r => r.join(",")).join("\n");

                          const blob = new Blob([csv], {
                            type: "text/csv;charset=utf-8;",
                          });

                          const link = document.createElement("a");
                          link.href = URL.createObjectURL(blob);
                          link.download = "BalanceSheet.csv";

                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);

                        } catch (err) {
                          console.error("CSV export error:", err);
                        }
                      },



                      // ✅ PDF EXPORT
                      exportPdf: async (columns, data) => {
                        try {
                          const visible = Helper.visibleColumn(columns);
                          if (!data?.length) return;

                          const headers = visible.map(col => {
                            const map = {
                              account: "Account Class",

                              as_on_date:
                                "As on Date\n" +
                                Helper.Dateview(currentDateText),

                              prev_month:
                                "Previous Month\n" +
                                Helper.Dateview(prevMonthDateText),

                              prev_year:
                                "Previous Year\n" +
                                Helper.Dateview(prevYearDateText),
                            };

                            return map[col.field] || col.field;
                          });



                          const body = data.map(row =>
                            visible.map(col => row[col.field] ?? "")
                          );

                          const doc = new jsPDF("l");

                          // ===== HEADER =====
                          doc.setFontSize(16);
                          doc.text("Balance Sheet", 14, 10);

                          doc.setFontSize(11);
                          doc.text(
                            `As on Date: ${Helper.Dateview(currentDateText)}`,
                            14,
                            16
                          );

                          // ===== TABLE =====
                          autoTable(doc, {
                            head: [headers],
                            body,
                            startY: 22,

                            styles: { fontSize: 8 },

                            headStyles: {
                              halign: "center",
                              valign: "middle",
                              fontStyle: "bold",
                            },
                          });


                          doc.save("BalanceSheet.pdf");

                        } catch (err) {
                          console.error("PDF export error:", err);
                        }
                      },



                      actionsColumnIndex: -1,
                      columnsButton: true,
                    }}
                    localization={{
                      toolbar: { searchPlaceholder: t("Search Balance Sheet") },
                    }}



                  />
                </div>

              </div>

              {/* FILTER PANEL */}
              <SlidingPane
                isOpen={state1.isPaneOpen}
                title="Filter By"
                onRequestClose={() => setState({ isPaneOpen: false })}
              >
                <div className="col-md-12">
                  <label className="col-form-label">{t("Company")}</label>
                  <select
                    className="form-select"
                    onChange={(e) => setSubcompany(e.target.value)}
                  >
                    <option value="">{t("Select Company")}</option>
                    {subcompanyOptions.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.sub_company_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-12 mt-2">
                  <label className="col-form-label">{t("Line of Business")}</label>
                  <select
                    className="form-select"
                    onChange={(e) => setLineOfBusiness(e.target.value)}
                  >
                    <option value="">{t("Select Line of Business")}</option>
                    {lineOfBusinessOptions.map((lob) => (
                      <option key={lob.id} value={lob.id}>
                        {lob.lob_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-12 mt-2">
                  <label className="col-form-label">{t("Contract")}</label>

                  <Select
                    value={selectedContract}
                    options={contractOptions}
                    onChange={handleContractChange}
                    onInputChange={handleContractInputChange}
                    onMenuScrollToBottom={handleContractScroll}
                    getOptionLabel={(e) => e.contract_number}
                    getOptionValue={(e) => e.id}
                    isLoading={contractLoading}
                    placeholder="Search Contract..."
                  />
                </div>

                <div className="col-md-12 mt-2">
                  <label className="col-form-label">{t("As on Date")}</label>
                  <DatePicker
                    className="form-control datepick"
                    selected={currentDate}
                    onChange={setCurrentDate}
                    name="date"
                    placeholderText={"mm/dd/yyyy"}
                    showYearDropdown
                    scrollableYearDropdown
                    showMonthDropdown
                    autoComplete="off"
                    dropdownMode="select"
                  />

                  <label className="col-form-label">{t("Period 1")}</label>
                  <DatePicker
                    className="form-control datepick"
                    selected={period1Date}
                    onChange={setPeriod1Date}
                    name="date"
                    placeholderText={"mm/dd/yyyy"}
                    showYearDropdown
                    scrollableYearDropdown
                    showMonthDropdown
                    autoComplete="off"
                    dropdownMode="select"
                  />

                  <label className="col-form-label">{t("Period 2")}</label>
                  <DatePicker
                    className="form-control datepick"
                    selected={period2Date}
                    onChange={setPeriod2Date}
                    name="date"
                    placeholderText={"mm/dd/yyyy"}
                    showYearDropdown
                    scrollableYearDropdown
                    showMonthDropdown
                    autoComplete="off"
                    dropdownMode="select"
                  />
                </div>



                {/* RADIO BUTTONS */}
                {/* <div className="col-md-12 mt-3">
                  <label className="col-form-label fw-bold">Compare With</label>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="compareMode"
                      value="current"
                      checked={compareMode === "current"}
                      onChange={() => setCompareMode("current")}
                    />
                    <label className="form-check-label">As on Date (Current)</label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="compareMode"
                      value="prevMonth"
                      checked={compareMode === "prevMonth"}
                      onChange={() => setCompareMode("prevMonth")}
                    />
                    <label className="form-check-label">Previous Month</label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="compareMode"
                      value="prevYear"
                      checked={compareMode === "prevYear"}
                      onChange={() => setCompareMode("prevYear")}
                    />
                    <label className="form-check-label">Previous Year</label>
                  </div>
                </div> */}

                <div className="mt-4">
                  <button
                    className="btn bg-gradient-secondary"
                    onClick={() => {
                      setSubcompany("");
                      setLineOfBusiness("");

                      // reset comparison dates
                      setCurrentDate(new Date());
                      setPeriod1Date(null);
                      setPeriod2Date(null);

                      // clear old data immediately (optional UX improvement)
                      setData(null);
                      setSelectedContract(null);
                      setContractOptions([]);
                      setContractInput("");
                      // re-fetch fresh data
                      fetchBalanceSheet();
                    }}

                  >
                    {t("Reset")}
                  </button>
                  &nbsp;&nbsp;
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      fetchBalanceSheet();
                      setState({ isPaneOpen: false });
                    }}
                  >
                    {t("Apply")}
                  </button>
                </div>
              </SlidingPane>
            </div>
          </div >
        </div>
      </div>
    </div>

  );
};

export default BalanceSheet;
